import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAppServiceMetaApi,
  getAppServiceByIdApi,
  addAppServiceApi,
  updateAppServiceApi,
  getAllStateApi,
  getCityByStateIdApi,
} from "../../../redux/api/api";
import ServiceIconUploader from "./ServiceIconUploader";
import ServicePreviewCard from "./ServicePreviewCard";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "mr", label: "Marathi" },
  { code: "hi", label: "Hindi" },
];
const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

const FALLBACK_META = {
  services: ["ride", "events", "movies", "sports", "food", "hotels", "bus", "train", "wallet", "shopping", "recharge", "other"],
  statuses: ["ACTIVE", "COMING_SOON", "INACTIVE"],
  actionTypes: ["SERVICE", "RIDE_BOOKING", "EVENT_LIST", "MOVIE_LIST", "OFFER_DETAILS", "IN_APP_SCREEN", "EXTERNAL_URL"],
  iconTypes: ["IMAGE", "ICON_KEY"],
  availabilityModes: ["GLOBAL", "LOCATION_BASED"],
  iconKeys: ["car", "taxi", "auto", "bike", "bus", "train", "ticket", "film", "wallet", "utensils", "building", "bag", "grid", "star"],
  iconSpec: { recommendedWidth: 512, recommendedHeight: 512, minWidth: 256, minHeight: 256, maxSizeMB: 2, formats: ["PNG", "WebP", "JPG"], aspectRatio: "1:1 (square)" },
  categories: [],
  subcategories: [],
};

const emptyLang = () => ({ en: "", mr: "", hi: "" });

const buildInitialForm = () => ({
  _id: null,
  serviceId: "",
  internalName: "",
  displayName: emptyLang(),
  subtitle: emptyLang(),
  icon: { type: "IMAGE", iconKey: "grid", objectKey: null, imageUrl: null, fit: "COVER" },
  card: { backgroundColor: "" },
  categoryId: "",
  subcategoryIds: [],
  businessServiceId: "",
  action: { type: "SERVICE", serviceId: "", value: "" },
  visibility: { showOnHome: true, showInServices: true, showInSearch: false, showComingSoon: true },
  displayOrder: "",
  status: "COMING_SOON",
  availability: { mode: "GLOBAL", stateIds: [], cityIds: [], areas: [], pincodes: [] },
});

const normalizeIncoming = (svc) => {
  const base = buildInitialForm();
  return {
    ...base,
    ...svc,
    displayName: { ...base.displayName, ...svc.displayName },
    subtitle: { ...base.subtitle, ...svc.subtitle },
    icon: { ...base.icon, ...svc.icon },
    card: { backgroundColor: svc.card?.backgroundColor || "" },
    action: { ...base.action, ...svc.action },
    visibility: { ...base.visibility, ...svc.visibility },
    categoryId: typeof svc.categoryId === "object" ? svc.categoryId?._id || "" : svc.categoryId || "",
    subcategoryIds: (svc.subcategoryIds || []).map((s) => (typeof s === "object" ? s._id : s)),
    displayOrder: svc.displayOrder ?? "",
    availability: {
      ...base.availability,
      ...svc.availability,
      stateIds: (svc.availability?.stateIds || []).map((s) => (typeof s === "object" ? s._id : s)),
      cityIds: (svc.availability?.cityIds || []).map((c) => (typeof c === "object" ? c._id : c)),
    },
  };
};

const CustomerAppServiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(buildInitialForm());
  const [meta, setMeta] = useState(FALLBACK_META);
  const [activeLang, setActiveLang] = useState("en");
  const [previewLang, setPreviewLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [areaInput, setAreaInput] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");

  useEffect(() => {
    getAppServiceMetaApi()
      .then((res) => {
        const info = res?.data?.info;
        if (info && Object.keys(info).length) setMeta({ ...FALLBACK_META, ...info });
      })
      .catch((error) =>
        toast.error(error?.response?.data?.message || "Couldn't load form options — showing the standard list."),
      );
    getAllStateApi()
      .then((res) => setStates(res?.data?.info || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    getAppServiceByIdApi(id)
      .then((res) => setForm(normalizeIncoming(res?.data?.info)))
      .catch(() => toast.error("Unable to load this service."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  useEffect(() => {
    const stateIds = form.availability.stateIds;
    if (!stateIds.length) {
      setCities([]);
      return;
    }
    Promise.all(stateIds.map((stateid) => getCityByStateIdApi({ stateid }).then((res) => res?.data?.info || [])))
      .then((results) => setCities(results.flat()))
      .catch(() => {});
  }, [form.availability.stateIds]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateIcon = (patch) => setForm((prev) => ({ ...prev, icon: { ...prev.icon, ...patch } }));
  const updateCard = (patch) => setForm((prev) => ({ ...prev, card: { ...prev.card, ...patch } }));
  const updateAction = (patch) => setForm((prev) => ({ ...prev, action: { ...prev.action, ...patch } }));
  const updateVisibility = (patch) => setForm((prev) => ({ ...prev, visibility: { ...prev.visibility, ...patch } }));
  const updateAvailability = (patch) => setForm((prev) => ({ ...prev, availability: { ...prev.availability, ...patch } }));
  const updateLangField = (field, lang, value) =>
    setForm((prev) => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));

  const selectedCategoryName = useMemo(
    () => meta.categories.find((c) => c._id === form.categoryId)?.name,
    [meta.categories, form.categoryId],
  );

  // Subcategory rows carry a free-text `category` name (the master hierarchy has
  // no hard FK) — best-effort filter to the picked category, fall back to all.
  const subcategoryOptions = useMemo(() => {
    if (!selectedCategoryName) return meta.subcategories;
    const filtered = meta.subcategories.filter(
      (s) => (s.category || "").toLowerCase() === selectedCategoryName.toLowerCase(),
    );
    return filtered.length ? filtered : meta.subcategories;
  }, [meta.subcategories, selectedCategoryName]);

  const addArea = () => {
    const value = areaInput.trim();
    if (value && !form.availability.areas.includes(value)) {
      updateAvailability({ areas: [...form.availability.areas, value] });
    }
    setAreaInput("");
  };
  const addPincode = () => {
    const value = pincodeInput.trim();
    if (!value) return;
    if (!PINCODE_PATTERN.test(value)) return toast.error("Enter a valid 6-digit Indian pincode.");
    if (!form.availability.pincodes.includes(value)) {
      updateAvailability({ pincodes: [...form.availability.pincodes, value] });
    }
    setPincodeInput("");
  };

  const buildPayload = () => ({
    id: form._id || undefined,
    serviceId: form.serviceId,
    internalName: form.internalName,
    displayName: form.displayName,
    subtitle: form.subtitle,
    icon:
      form.icon.type === "IMAGE"
        ? {
            type: "IMAGE",
            iconKey: form.icon.iconKey || "grid",
            objectKey: form.icon.objectKey,
            imageUrl: form.icon.imageUrl,
            fit: form.icon.fit === "CONTAIN" ? "CONTAIN" : "COVER",
          }
        : { type: "ICON_KEY", iconKey: form.icon.iconKey || "grid", objectKey: null, imageUrl: null, fit: "COVER" },
    card: { backgroundColor: (form.card.backgroundColor || "").trim() || null },
    categoryId: form.categoryId || null,
    subcategoryIds: form.subcategoryIds,
    businessServiceId: form.businessServiceId || form.serviceId,
    action: {
      type: form.action.type || "SERVICE",
      serviceId: form.action.serviceId || form.serviceId,
      value: form.action.value || "",
    },
    visibility: form.visibility,
    displayOrder: form.displayOrder === "" ? undefined : Number(form.displayOrder),
    status: form.status,
    availability: form.availability,
  });

  const validate = () => {
    if (!form.serviceId) return "Pick an Internal Service.";
    if (!form.internalName.trim()) return "Internal Name is required.";
    const bg = (form.card.backgroundColor || "").trim();
    if (bg && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(bg)) {
      return "Card background must be a hex colour like #F2ECEE (or leave it blank).";
    }
    if (form.status === "ACTIVE") {
      if (!form.displayName.en.trim()) return "English Display Name is required to activate this service.";
      if (form.icon.type === "IMAGE" && !form.icon.imageUrl) return "Upload an icon image, or switch to an icon key.";
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) return toast.error(error);
    setSaving(true);
    try {
      if (form._id) {
        await updateAppServiceApi(buildPayload());
      } else {
        const response = await addAppServiceApi(buildPayload());
        const created = response?.data?.info;
        setForm((prev) => ({ ...prev, _id: created?._id }));
      }
      toast.success("Service saved.");
      navigate("/app-config/customer-app-services");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to save service.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading service…</Typography>
      </Box>
    );
  }

  const previewService = {
    displayName: form.displayName[previewLang] || form.displayName.en || form.internalName,
    subtitle: form.subtitle[previewLang] || form.subtitle.en,
    icon: form.icon,
    backgroundColor: (form.card.backgroundColor || "").trim() || null,
    status: form.status,
  };

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>{isEdit ? "Edit Customer App Service" : "New Customer App Service"}</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>App Configuration</li>
          <li>
            <Link to="/app-config/customer-app-services">Customer App Services</Link>
          </li>
          <li>{isEdit ? "Edit" : "New"}</li>
        </ul>
      </div>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Card sx={{ flex: "1 1 620px" }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Identity */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl required sx={{ minWidth: 220, flex: 1 }}>
                <InputLabel>Internal Service</InputLabel>
                <Select
                  label="Internal Service"
                  value={form.serviceId}
                  onChange={(e) =>
                    updateForm({
                      serviceId: e.target.value,
                      businessServiceId: form.businessServiceId || e.target.value,
                      action: { ...form.action, serviceId: form.action.serviceId || e.target.value },
                    })
                  }
                >
                  {meta.services.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Internal Name"
                required
                sx={{ flex: 1, minWidth: 220 }}
                value={form.internalName}
                onChange={(e) => updateForm({ internalName: e.target.value })}
              />
            </Box>

            {/* Localised customer copy */}
            <Box>
              <Tabs value={activeLang} onChange={(_, v) => setActiveLang(v)} sx={{ mb: 1.5 }}>
                {LANGUAGES.map((lang) => (
                  <Tab key={lang.code} value={lang.code} label={form.displayName[lang.code] ? `${lang.label} ✓` : lang.label} />
                ))}
              </Tabs>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label={`Customer Display Name (${activeLang.toUpperCase()})`}
                  value={form.displayName[activeLang]}
                  onChange={(e) => updateLangField("displayName", activeLang, e.target.value)}
                />
                <TextField
                  label={`Customer Subtitle (${activeLang.toUpperCase()})`}
                  value={form.subtitle[activeLang]}
                  onChange={(e) => updateLangField("subtitle", activeLang, e.target.value)}
                />
              </Box>
            </Box>

            <Divider />

            {/* Mapping */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ flex: 1, minWidth: 220 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.categoryId}
                  onChange={(e) => updateForm({ categoryId: e.target.value, subcategoryIds: [] })}
                >
                  <MenuItem value="">— None —</MenuItem>
                  {meta.categories.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 220 }}>
                <InputLabel>Subcategories</InputLabel>
                <Select
                  multiple
                  label="Subcategories"
                  value={form.subcategoryIds}
                  onChange={(e) => updateForm({ subcategoryIds: e.target.value })}
                  renderValue={(selected) =>
                    subcategoryOptions
                      .filter((s) => selected.includes(s._id))
                      .map((s) => s.subcategory_name)
                      .join(", ")
                  }
                >
                  {subcategoryOptions.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.subcategory_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Business Service"
                sx={{ flex: 1, minWidth: 200 }}
                helperText="Which backend module fulfils this — defaults to the internal service id."
                value={form.businessServiceId}
                onChange={(e) => updateForm({ businessServiceId: e.target.value })}
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Action Type</InputLabel>
                <Select label="Action Type" value={form.action.type} onChange={(e) => updateAction({ type: e.target.value })}>
                  {meta.actionTypes.map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={form.action.type === "EXTERNAL_URL" || form.action.type === "IN_APP_SCREEN" ? "Action Target" : "Action Service Id"}
                sx={{ flex: 1, minWidth: 200 }}
                value={form.action.type === "EXTERNAL_URL" || form.action.type === "IN_APP_SCREEN" ? form.action.value : form.action.serviceId}
                onChange={(e) =>
                  form.action.type === "EXTERNAL_URL" || form.action.type === "IN_APP_SCREEN"
                    ? updateAction({ value: e.target.value })
                    : updateAction({ serviceId: e.target.value })
                }
              />
            </Box>

            <Divider />

            {/* Icon */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                Icon
              </Typography>
              {form.icon.type === "IMAGE" ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Recommended: {(meta.iconSpec?.recommendedWidth) || 512} × {(meta.iconSpec?.recommendedHeight) || 512} px, square
                  (1:1) · PNG / WebP / JPG · max {(meta.iconSpec?.maxSizeMB) || 2} MB
                </Typography>
              ) : null}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={form.icon.type}
                onChange={(_, v) => v && updateIcon({ type: v })}
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="IMAGE">Upload Image</ToggleButton>
                <ToggleButton value="ICON_KEY">Icon Key</ToggleButton>
              </ToggleButtonGroup>
              {form.icon.type === "IMAGE" ? (
                <>
                  <ServiceIconUploader
                    serviceId={form.serviceId}
                    value={{ imageUrl: form.icon.imageUrl, fileSize: form.icon.fileSize, width: form.icon.width, height: form.icon.height }}
                    onUploaded={(info) =>
                      updateIcon({
                        objectKey: info.objectKey,
                        imageUrl: info.imageUrl,
                        fileSize: info.fileSize,
                        width: info.width,
                        height: info.height,
                      })
                    }
                    onRemove={() => updateIcon({ objectKey: null, imageUrl: null, fileSize: null, width: null, height: null })}
                  />
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Image fit
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={form.icon.fit || "COVER"}
                      onChange={(_, v) => v && updateIcon({ fit: v })}
                    >
                      <ToggleButton value="COVER">Fill card</ToggleButton>
                      <ToggleButton value="CONTAIN">Fit whole image</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {form.icon.fit === "CONTAIN"
                        ? "Whole image stays visible (letterboxed on the card colour) — best for logos."
                        : "Image fills the whole card, edges cropped — best for photos / full-bleed art. Removes empty space."}
                    </Typography>
                  </Box>
                </>
              ) : (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Icon Key</InputLabel>
                  <Select label="Icon Key" value={form.icon.iconKey} onChange={(e) => updateIcon({ iconKey: e.target.value })}>
                    {meta.iconKeys.map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Card Background
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Keep it soft — the icon/image should stand out, not the panel. Leave blank for the app default.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.card.backgroundColor || "") ? form.card.backgroundColor : "#F2ECEE"}
                    onChange={(e) => updateCard({ backgroundColor: e.target.value })}
                    style={{ width: 44, height: 36, border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", padding: 0 }}
                  />
                  <TextField
                    size="small"
                    placeholder="#F2ECEE"
                    value={form.card.backgroundColor || ""}
                    onChange={(e) => updateCard({ backgroundColor: e.target.value })}
                    sx={{ width: 130 }}
                  />
                  {["#F2ECEE", "#EEF1F6", "#EAF3EE", "#FBF3E7", "#F1ECF8", "#F5F5F4"].map((c) => (
                    <Box
                      key={c}
                      onClick={() => updateCard({ backgroundColor: c })}
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        bgcolor: c,
                        border: "1px solid #ddd",
                        cursor: "pointer",
                        outline: (form.card.backgroundColor || "").toLowerCase() === c.toLowerCase() ? "2px solid #1976d2" : "none",
                      }}
                    />
                  ))}
                  {form.card.backgroundColor ? (
                    <Button size="small" onClick={() => updateCard({ backgroundColor: "" })}>
                      Reset
                    </Button>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Placement + status */}
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                label="Display Order"
                type="number"
                sx={{ width: 150 }}
                value={form.displayOrder}
                onChange={(e) => updateForm({ displayOrder: e.target.value })}
              />
              <FormControlLabel
                control={<Switch checked={form.visibility.showOnHome} onChange={(e) => updateVisibility({ showOnHome: e.target.checked })} />}
                label="Show on Home"
              />
              <FormControlLabel
                control={
                  <Switch checked={form.visibility.showInServices} onChange={(e) => updateVisibility({ showInServices: e.target.checked })} />
                }
                label="Show in Services"
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status} onChange={(e) => updateForm({ status: e.target.value })}>
                  {meta.statuses.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s === "COMING_SOON" ? "Coming Soon" : s.charAt(0) + s.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Availability */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Service Availability
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                “Can this service operate here?” — separate from advertisement targeting.
              </Typography>
              <RadioGroup
                row
                value={form.availability.mode}
                onChange={(e) => updateAvailability({ mode: e.target.value })}
                sx={{ mb: 1 }}
              >
                <FormControlLabel value="GLOBAL" control={<Radio />} label="All India (Global)" />
                <FormControlLabel value="LOCATION_BASED" control={<Radio />} label="Location-based" />
              </RadioGroup>

              {form.availability.mode === "LOCATION_BASED" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 620 }}>
                  <FormControl>
                    <InputLabel>States</InputLabel>
                    <Select
                      multiple
                      label="States"
                      value={form.availability.stateIds}
                      onChange={(e) => updateAvailability({ stateIds: e.target.value, cityIds: [] })}
                      renderValue={(selected) =>
                        states.filter((s) => selected.includes(s._id)).map((s) => s.name).join(", ")
                      }
                    >
                      {states.map((s) => (
                        <MenuItem key={s._id} value={s._id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl disabled={!cities.length}>
                    <InputLabel>Cities</InputLabel>
                    <Select
                      multiple
                      label="Cities"
                      value={form.availability.cityIds}
                      onChange={(e) => updateAvailability({ cityIds: e.target.value })}
                      renderValue={(selected) =>
                        cities.filter((c) => selected.includes(c._id || c.id)).map((c) => c.name).join(", ")
                      }
                    >
                      {cities.map((c) => (
                        <MenuItem key={c._id || c.id} value={c._id || c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Areas
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}>
                      {form.availability.areas.map((a) => (
                        <Chip
                          key={a}
                          label={a}
                          size="small"
                          onDelete={() => updateAvailability({ areas: form.availability.areas.filter((x) => x !== a) })}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g. Dharampeth"
                        value={areaInput}
                        onChange={(e) => setAreaInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addArea())}
                      />
                      <Button onClick={addArea}>+ Add</Button>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Pincodes
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}>
                      {form.availability.pincodes.map((p) => (
                        <Chip
                          key={p}
                          label={p}
                          size="small"
                          onDelete={() => updateAvailability({ pincodes: form.availability.pincodes.filter((x) => x !== p) })}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g. 440010"
                        value={pincodeInput}
                        onChange={(e) => setPincodeInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPincode())}
                      />
                      <Button onClick={addPincode}>+ Add</Button>
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
              <Button onClick={() => navigate("/app-config/customer-app-services")}>Cancel</Button>
              <Button variant="contained" disabled={saving} onClick={handleSave}>
                {saving ? "Saving…" : "Save Service"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Live customer preview */}
        <Card sx={{ flex: "0 1 320px", position: "sticky", top: 16 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Customer App Preview
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Approximate — the app owns the real layout, animation and typography.
            </Typography>
            <Tabs value={previewLang} onChange={(_, v) => setPreviewLang(v)} sx={{ mb: 2 }}>
              {LANGUAGES.map((l) => (
                <Tab key={l.code} value={l.code} label={l.code.toUpperCase()} />
              ))}
            </Tabs>
            <Box
              sx={{
                bgcolor: "#FFF7F8",
                borderRadius: 3,
                p: 2,
                border: "1px solid #f0d9de",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280" }}>
                Top Services
              </Typography>
              <Box sx={{ mt: 1 }}>
                <ServicePreviewCard service={previewService} />
              </Box>
            </Box>
            {form.status !== "ACTIVE" ? (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 2 }}>
                Status is “{form.status === "COMING_SOON" ? "Coming Soon" : "Inactive"}” — this tile will NOT appear on the customer
                Home screen until it is set to Active.
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default CustomerAppServiceForm;
